import type { KeyboardEvent } from "react";
import { clamp, cx } from "./utils";

export interface StarsProps {
  /** 1–5, or `null` when not yet rated. */
  value: number | null;
  /** Omit to render read-only. */
  onChange?: (value: number) => void;
  /** @default 5 */
  max?: number;
  /** Accessible name for the group. */
  label: string;
  /** Render non-interactive. */
  readOnly?: boolean;
  /** @default "md" */
  size?: "sm" | "md" | "lg";
  className?: string;
}

const STAR = "M12 2.8l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.66 6.2 20.7l1.1-6.45-4.7-4.6 6.5-.95z";

/**
 * 1–5 star rating rendered as a slider role so arrow keys work
 * (←/↓ decrement, →/↑ increment, Home/End jump to the ends).
 */
export function Stars({
  value,
  onChange,
  max = 5,
  label,
  readOnly = false,
  size = "md",
  className,
}: StarsProps) {
  const interactive = !readOnly && !!onChange;
  const set = (v: number) => onChange?.(clamp(Math.round(v), 1, max));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    const cur = value ?? 0;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      set(cur - 1 < 1 ? 1 : cur - 1);
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      set(cur + 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      set(1);
    } else if (e.key === "End") {
      e.preventDefault();
      set(max);
    } else if (/^[1-9]$/.test(e.key)) {
      e.preventDefault();
      set(Number(e.key));
    }
  };

  return (
    <div
      className={cx("xz-stars", `xz-stars--${size}`, !interactive && "is-readonly", className)}
      role="slider"
      aria-label={label}
      aria-valuemin={1}
      aria-valuemax={max}
      aria-valuenow={value ?? undefined}
      aria-valuetext={value == null ? "未评分" : `${value} / ${max}`}
      aria-readonly={!interactive || undefined}
      tabIndex={interactive ? 0 : -1}
      onKeyDown={onKeyDown}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          className={cx("xz-stars__star", value != null && n <= value && "is-on")}
          onClick={interactive ? () => set(n) : undefined}
        >
          <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
            <path d={STAR} fill="currentColor" />
          </svg>
        </span>
      ))}
    </div>
  );
}
